import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Trash2, Lock } from 'lucide-react';
import { ProductSearchCombobox } from './bon-travail/ProductSearchCombobox';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function SectionProduits({ items, onItemsChange, lockedProducts }) {
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produitsParFournisseur, setProduitsParFournisseur] = useState({});
  const [afficherSousTotal, setAfficherSousTotal] = useState(true);

  const fetchFournisseurs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fournisseurs?actif=true`);
      if (res.status === 200) {
        const data = res.data;
        setFournisseurs(data.fournisseurs || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchFournisseurs(); }, [fetchFournisseurs]);

  const fetchProduits = useCallback(async (fournisseurId) => {
    if (produitsParFournisseur[fournisseurId]) return;
    try {
      const res = await axios.get(`${API_URL}/api/produits?actif=true&fournisseur_id=${fournisseurId}`);
      if (res.status === 200) {
        const data = res.data;
        setProduitsParFournisseur(prev => ({ ...prev, [fournisseurId]: data.produits || [] }));
      }
    } catch (e) { console.error(e); }
  }, [produitsParFournisseur]);

  const ajouterLigne = () => {
    onItemsChange([...items, {
      fournisseur_id: '',
      produit_id: '',
      description: '',
      unite: 'gallon',
      quantite: 1,
      prix_unitaire: 0,
      montant: 0,
    }]);
  };

  const supprimerLigne = (idx) => {
    onItemsChange(items.filter((_, i) => i !== idx));
  };

  const modifierItem = (idx, champ, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [champ]: val };

    // Quand on sélectionne un fournisseur, charger ses produits
    if (champ === 'fournisseur_id' && val) {
      fetchProduits(val);
      updated[idx].produit_id = '';
      updated[idx].description = '';
      updated[idx].prix_unitaire = 0;
      updated[idx].montant = 0;
    }

    // Quand on sélectionne un produit, remplir les infos
    if (champ === 'produit_id' && val) {
      const fId = updated[idx].fournisseur_id;
      const produit = (produitsParFournisseur[fId] || []).find(p => p.id === val);
      if (produit) {
        updated[idx].description = produit.nom;
        updated[idx].unite = produit.unite;
        updated[idx].prix_unitaire = produit.prix_vendu || 0;
        updated[idx].montant = (updated[idx].quantite || 1) * (produit.prix_vendu || 0);
        updated[idx].code_produit = produit.code_produit || '';
      }
    }

    // Recalculer montant quand quantité ou unité change
    if (champ === 'quantite') {
      const qte = parseFloat(val) || 0;
      updated[idx].quantite = qte;
      updated[idx].montant = qte * (updated[idx].prix_unitaire || 0);
    }

    if (champ === 'unite') {
      updated[idx].unite = val;
    }

    onItemsChange(updated);
  };

  const sousTotal = items.reduce((sum, item) => sum + (parseFloat(item.montant) || 0), 0);

  const uniteLabel = (u) => ({ pinte: 'Pinte', gallon: 'Gallon', chaudiere: 'Chaudière' }[u] || u);

  return (
    <Card data-testid="section-produits">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-2xl text-center flex-1 font-bold">Produits</CardTitle>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sous-total-produits"
              checked={afficherSousTotal}
              onChange={(e) => setAfficherSousTotal(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="sous-total-produits" className="text-sm cursor-pointer">
              Afficher sous-total
            </label>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-200">
              <th className="p-3 text-left font-semibold border w-48">Fournisseur</th>
              <th className="p-3 text-left font-semibold border">Produit</th>
              <th className="p-3 text-center font-semibold border w-32">Unité</th>
              <th className="p-3 text-center font-semibold border w-24">Qté</th>
              <th className="p-3 text-right font-semibold border w-28">Prix unit.</th>
              <th className="p-3 text-right font-semibold border w-28">Total</th>
              <th className="p-3 border w-14"></th>
            </tr>
          </thead>
          <tbody className="bg-gray-50">
            {items.map((item, idx) => {
              const produitsDuFournisseur = produitsParFournisseur[item.fournisseur_id] || [];
              const isLocked = item.verrouille || (lockedProducts && item.commande_initiale_faite);
              return (
                <tr key={item.id || item._id || `item-${idx}`} className={`border ${isLocked ? 'bg-emerald-50 opacity-80' : ''}`}>
                  {/* Fournisseur */}
                  <td className="p-2 border">
                    {isLocked ? (
                      <div className="flex items-center gap-1 text-sm text-emerald-700 px-2 py-1"><Lock className="w-3 h-3" />{fournisseurs.find(f => f.id === item.fournisseur_id)?.nom || '—'}</div>
                    ) : (
                    <Select
                      value={item.fournisseur_id || ''}
                      onValueChange={(val) => modifierItem(idx, 'fournisseur_id', val)}
                    >
                      <SelectTrigger className="bg-white" data-testid={`produit-fournisseur-${idx}`}>
                        <SelectValue placeholder="Fournisseur..." />
                      </SelectTrigger>
                      <SelectContent>
                        {fournisseurs.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </td>
                  {/* Produit */}
                  <td className="p-2 border">
                    {isLocked ? (
                      <div className="flex items-center gap-1 text-sm text-emerald-700 px-2 py-1"><Lock className="w-3 h-3" />{item.description || '—'}</div>
                    ) : (
                    <ProductSearchCombobox
                      produits={produitsDuFournisseur}
                      value={item.produit_id}
                      onSelect={(prod) => modifierItem(idx, 'produit_id', prod.id)}
                      disabled={!item.fournisseur_id}
                      placeholder={item.fournisseur_id ? "Rechercher produit..." : "Sélectionner fournisseur d'abord"}
                      testId={`produit-select-${idx}`}
                    />
                    )}
                  </td>
                  {/* Unité — 🐛 [Phase 94 — 2026-07-08] LECTURE SEULE :
                    * L'unité est verrouillée sur celle du produit choisi. Le catalogue
                    * a un produit distinct par unité (ex: « Sico Blanc — Gallon » vs
                    * « Sico Blanc — Chaudière ») → changer l'unité manuellement ferait
                    * référencer le mauvais produit et un mauvais prix dans les stats.
                    * Pour changer d'unité, sélectionner un autre produit.
                    */}
                  <td className="p-2 border">
                    <div
                      className="flex items-center justify-center bg-gray-100 text-gray-700 text-sm rounded px-3 py-2 border border-gray-200"
                      data-testid={`produit-unite-${idx}`}
                      title="Verrouillé — l'unité est définie par le produit choisi"
                    >
                      🔒 {uniteLabel(item.unite || 'gallon')}
                    </div>
                  </td>
                  {/* Quantité */}
                  <td className="p-2 border">
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      className={`text-center ${isLocked ? 'bg-emerald-50' : 'bg-white'}`}
                      value={item.quantite || 1}
                      onChange={(e) => modifierItem(idx, 'quantite', e.target.value)}
                      disabled={isLocked}
                      data-testid={`produit-qte-${idx}`}
                    />
                  </td>
                  {/* Prix unitaire */}
                  <td className="p-2 border text-right font-medium text-gray-700">
                    {(item.prix_unitaire || 0).toFixed(2)} $
                  </td>
                  {/* Total */}
                  <td className="p-2 border text-right font-semibold">
                    {(item.montant || 0).toFixed(2)} $
                  </td>
                  {/* Supprimer */}
                  <td className="p-2 border text-center">
                    {isLocked ? (
                      <span className="text-xs text-emerald-600 font-medium">Verrouillé</span>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => supprimerLigne(idx)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}

            {/* Sous-total */}
            {afficherSousTotal && items.length > 0 && (
              <tr className="bg-gray-200 font-bold border">
                <td colSpan={5} className="p-3 text-left">Sous-total Produits</td>
                <td className="p-3 text-right text-lg">{sousTotal.toFixed(2)} $</td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="mt-4">
          <Button
            type="button"
            className="bg-blue-600 hover:bg-blue-700 text-white w-full"
            onClick={ajouterLigne}
            data-testid="add-produit-line-btn"
          >
            + Ajouter un produit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
